import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@bbpos/ui';
import { categoryService } from '@/services/category.service';
import { cn } from '@/constants/design-tokens';
import { filterCategories, createOptionFor, isSameCategory } from './category-combobox-logic';

// Inline SVG icons — the admin app has no icon library dependency.
function Icon({ d, className }: { d: string; className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

const CHEVRON_DOWN = 'M6 9l6 6 6-6';
const PLUS = 'M12 5v14M5 12h14';
const CHECK = 'M20 6L9 17l-5-5';

/**
 * The names of the active category master rows (Admin → Categories), sorted.
 * A product's category must be one of them (C03), so every picker that sets
 * one — this combobox, the product list's bulk Change Category — reads this.
 */
export function useCategoryMasterNames() {
  return useQuery({
    queryKey: ['category-master-names'],
    queryFn: async () => {
      const rows = await categoryService.list({ is_active: true }).then((r) => r.data).catch(() => []);
      return [...new Set(rows.map((c) => c.name))].sort((a, b) => a.localeCompare(b));
    },
    staleTime: 60_000,
  });
}

/**
 * Category picker for the product forms.
 *
 * Replaces the plain text input (product detail) and the native `<datalist>`
 * (product create). A datalist gives no visible affordance that options exist
 * and cannot offer a "create" action, so shopkeepers could not tell the field
 * was anything but free text — and every typo silently became a new category.
 *
 * Behaviour: click to see existing categories, type to filter, and when the
 * typed name is new, a "+ Create ..." row persists it to the categories table
 * immediately so it is reusable across products.
 *
 * C03: only rows of the category master (Admin → Categories) are offered — a
 * product's category must be one of them, and the API refuses any other name.
 */
export function CategoryCombobox({
  id,
  value,
  onChange,
  placeholder = 'Select or type a category',
  disabled = false,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // The active rows of the category master only (C03) — never loose names
  // that products happen to carry.
  const { data: categories } = useCategoryMasterNames();

  const options = useMemo(() => categories ?? [], [categories]);
  const matches = useMemo(() => filterCategories(options, query), [options, query]);
  const createName = createOptionFor(options, query);

  const createCategory = useMutation({
    mutationFn: (name: string) => categoryService.create({ name }),
    onSuccess: (_created, name) => {
      // Refresh the shared suggestion list so the new name is offered elsewhere.
      queryClient.invalidateQueries({ queryKey: ['category-master-names'] });
      queryClient.invalidateQueries({ queryKey: ['product-categories-all'] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      onChange(name);
      setQuery('');
      setOpen(false);
      toast({ title: `Category "${name}" created`, variant: 'success' });
    },
    onError: (err: unknown) => {
      // Nothing is kept: a name that is not in the category list would be
      // refused when the product is saved (C03).
      setQuery('');
      setOpen(false);
      toast({
        title: err instanceof Error ? err.message : 'Could not create category',
        variant: 'error',
      });
    },
  });

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  function select(name: string) {
    onChange(name);
    setQuery('');
    setOpen(false);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!open) return;
      // Enter commits the single obvious match, else creates the typed name.
      if (matches.length === 1) select(matches[0]);
      else if (createName) createCategory.mutate(createName);
      return;
    }
    if (!open && (e.key === 'ArrowDown' || e.key.length === 1)) setOpen(true);
  }

  // While open the input shows what is being typed; when closed it shows the
  // committed value.
  const shown = open ? query : value;

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <input
          id={id}
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          autoComplete="off"
          disabled={disabled}
          value={shown}
          placeholder={placeholder}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          className={cn(
            'w-full rounded-lg border px-3 py-2 pr-9 text-sm transition-all duration-200',
            'border-neutral-300 bg-white text-neutral-900 placeholder:text-neutral-400',
            'focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30',
            'dark:border-neutral-700 dark:bg-neutral-800 dark:text-white dark:placeholder:text-neutral-500',
            disabled && 'cursor-not-allowed opacity-60',
          )}
        />
        <button
          type="button"
          tabIndex={-1}
          aria-label="Toggle category list"
          disabled={disabled}
          onClick={() => {
            setOpen((o) => !o);
            setQuery('');
            inputRef.current?.focus();
          }}
          className="absolute inset-y-0 right-0 flex items-center px-2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200"
        >
          <Icon
            d={CHEVRON_DOWN}
            className={cn('h-4 w-4 transition-transform duration-200', open && 'rotate-180')}
          />
        </button>
      </div>

      {open && (
        <ul
          role="listbox"
          className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-neutral-200 bg-white py-1 shadow-lg animate-fade-in dark:border-neutral-700 dark:bg-neutral-800"
        >
          {matches.map((name) => {
            const selected = isSameCategory(name, value);
            return (
              <li key={name}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => select(name)}
                  className={cn(
                    'flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors',
                    'hover:bg-brand-50 dark:hover:bg-neutral-700',
                    selected
                      ? 'font-medium text-brand-700 dark:text-brand-300'
                      : 'text-neutral-700 dark:text-neutral-200',
                  )}
                >
                  <span className="truncate">{name}</span>
                  {selected && <Icon d={CHECK} className="h-3.5 w-3.5 shrink-0" />}
                </button>
              </li>
            );
          })}

          {createName && (
            <li className={cn(matches.length > 0 && 'border-t border-neutral-100 dark:border-neutral-700')}>
              <button
                type="button"
                disabled={createCategory.isPending}
                onClick={() => createCategory.mutate(createName)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-brand-600 transition-colors hover:bg-brand-50 disabled:opacity-60 dark:text-brand-400 dark:hover:bg-neutral-700"
              >
                <Icon
                  d={PLUS}
                  className={cn('h-3.5 w-3.5 shrink-0', createCategory.isPending && 'animate-spin')}
                />
                <span className="truncate">Create &ldquo;{createName}&rdquo;</span>
              </button>
            </li>
          )}

          {matches.length === 0 && !createName && (
            <li className="px-3 py-2 text-sm text-neutral-400 dark:text-neutral-500">
              No categories yet — type a name to create one.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
